import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, delay, map} from 'rxjs/operators';

import {Logger} from 'src/app/core/interfaces/Logger';
import {IBookingCheckStatusResponse} from '../../domain/interfaces/booking-check-status-response.interface';
import {IBookingUnLockRequestPayload} from '../../domain/interfaces/booking-unlock-request.payload';
import {IBookingUnLockResponse} from '../../domain/interfaces/booking-unlock-response.interface';
import {Booking} from '../../domain/models/booking.model';
import {BookingRepository} from '../../domain/repositories/booking.repository';
import {BookingMapper} from '../mappers/booking.mapper';
import {activeBookingMocks, bookingMocks} from '../mocks/booking.mock';
import {ICloseBookingPayload} from "../../application/builders/close-booking-payload.builder";

@Injectable({providedIn: 'root'})
export class BookingMockService implements BookingRepository {
  private readonly logger = inject(Logger);
  private readonly delayTime = this.getRandomDelay();

  getAll(): Observable<Booking[]> {
    this.log('getAll', 'Returning mock bookings');

    return of(bookingMocks).pipe(
      delay(this.delayTime),
      map(mocks => mocks.map(BookingMapper.fromJSON)),
      catchError(error => this.handleError(error, 'Error fetching mock bookings', []))
    );
  }

  getById(bookingId: string): Observable<Booking | null> {
    this.log('getById', `Fetching mock booking with ID: ${bookingId}`);

    const mock = bookingMocks.find(b => b.id === bookingId) ?? null;

    return of(mock).pipe(
      delay(this.delayTime),
      map(m => (m ? BookingMapper.fromJSON(m) : null)),
      catchError(error => this.handleError(error, `Error fetching mock booking ${bookingId}`, null))
    );
  }

  getActive(): Observable<Booking | null> {
    this.log('getActive', 'Fetching active mock booking');

    const active = activeBookingMocks[0] ?? null;

    return of(active).pipe(
      delay(this.delayTime),
      map(booking => (booking ? BookingMapper.fromJSON(booking) : null)),
      catchError(error => this.handleError(error, 'Error fetching active mock booking', null))
    );
  }

  activate(bookingId: string): Observable<Booking | null> {
    this.log('activate', `Activating mock booking with ID: ${bookingId}`);

    return this.getById(bookingId);
  }

  update(booking: Booking): Observable<Booking | null> {
    this.log('update', `Mock updating booking ${booking.id}`);

    return of(booking).pipe(
      delay(this.delayTime),
      catchError(error => this.handleError(error, `Error updating mock booking ${booking.id}`, null))
    );
  }

  close(payload: ICloseBookingPayload): Observable<void> {
    this.log('release', `Mock releasing booking ${payload.id}`);

    return of(void 0).pipe(
      delay(this.delayTime),
      catchError(error => this.handleError(error, `Error releasing mock booking ${payload.id}`, void 0))
    );
  }

  unlock(payload: IBookingUnLockRequestPayload): Observable<IBookingUnLockResponse> {
    this.log('unlock', `Unlocking mock booking ${payload.bookingId} for device ${payload.deviceId}`);

    const exists = bookingMocks.some(booking => booking.id === payload.bookingId);

    return of({unlocked: exists}).pipe(
      delay(this.delayTime),
      catchError(error =>
        this.handleError<IBookingUnLockResponse>(
          error,
          `Error unlocking mock booking ${payload.bookingId}`,
          ({unlocked: false} satisfies IBookingUnLockResponse)
        )
      )
    );
  }

  checkStatus(bookingId: string): Observable<IBookingCheckStatusResponse> {
    this.log('checkStatus', `Checking status of mock booking ${bookingId}`);

    return of({deviceName: ''} satisfies IBookingCheckStatusResponse);
  }

  private getRandomDelay(): number {
    return Math.random() * 1000 + 500;
  }

  private log(operation: string, message: string): void {
    this.logger.debug(`[BookingMockService][${operation}] ${message}`);
  }

  private handleError<T>(error: Error, message: string, fallback: T): Observable<T> {
    this.logger.error(error, message);

    return of(fallback);
  }
}
